import { useEffect, useLayoutEffect, useState } from "react";
import { isTheme, type Theme, ThemeProviderContext } from "./theme-context";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * Rendered values of the `--background` token per mode. The installed PWA's
 * titlebar takes its color from `<meta name="theme-color">`, which can't
 * reference CSS variables — so the hexes are duplicated here and in the
 * pre-paint inline script in index.html. Update all three together with any
 * `--background` change.
 */
const THEME_COLORS = { light: "#F7F7F7", dark: "#141414" } as const;

function applyThemeColorMeta(resolved: "light" | "dark") {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[resolved]);
}

/**
 * Swap the theme class with CSS transitions disabled for one frame. Otherwise
 * every element with `transition-colors`/`transition-all` animates its color
 * change on toggle — so some parts of the page (transitioned) visibly lag
 * behind others (untransitioned). Killing transitions makes the whole page
 * repaint in a single frame, then restores them for hover/focus states.
 */
function applyThemeClass(resolved: "light" | "dark") {
  const root = document.documentElement;
  const style = document.createElement("style");
  style.appendChild(
    document.createTextNode("*,*::before,*::after{transition:none !important}"),
  );
  document.head.appendChild(style);

  root.classList.remove("light", "dark");
  root.classList.add(resolved);
  applyThemeColorMeta(resolved);

  // Force a synchronous reflow so the "transition:none" override is committed
  // before the class change paints, then drop it after the frame.
  void window.getComputedStyle(root).opacity;
  window.requestAnimationFrame(() => {
    style.remove();
  });
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "sameday-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(storageKey) as Theme;
    return isTheme(storedTheme) ? storedTheme : defaultTheme;
  });

  // useLayoutEffect runs synchronously before the browser paints, so the
  // theme class is always correct on first visible frame — no FOUC.
  useLayoutEffect(() => {
    applyThemeClass(resolveTheme(theme));
  }, [theme]);

  // Listen for OS-level color-scheme changes when set to "system".
  useEffect(() => {
    if (theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      applyThemeClass(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
